import React, { useState, useEffect } from 'react';
import { ReactComponent as EmptyIcon } from "../../../assets/icons/Empty.svg";
import { ReactComponent as RestoreIcon } from "../../../assets/icons/repeatIcon.svg";
import arrowRightIcon from "../../../assets/icons/arrowRight.png";
import { ReactComponent as InfoGreyIcon } from "../../../assets/icons/infoGrey.svg";
import { ReactComponent as InfoPrimaryIcon } from "../../../assets/icons/infoPrimary.svg";
import QuillReactEditor from "../../../components/QuillReactEditor";
import TemplateTableGenerator from "../../../pages/libraries/TemplateTableGenerator";
import { getPopulateFSDeletedItemsApiHandler, restorePopulateFSDeletedItemsApiHandler } from "../../../../WorkSpace/Components/ApiServices/apiHandler";
import ErrorPopup from "../../../elements/ErrorPopup";
import RestorePopup from "../../../elements/RestorePopup";
import SuccessPopup from "../../../elements/SuccessPopup";

const genId = () => Math.random().toString(36).slice(2);

const normalizeFromInitial = (initialDoc) => {
  const items = [];
  (initialDoc?.data || []).forEach((block) => {
    if (block.dataType === "text") {
      items.push({
        id: block._id || genId(),
        type: "text",
        dataType: "text",
        textFormat: "html",
        textRaw: block?.textRaw ?? " ",
        textPlain: block?.textPlain ?? " ",
        text: [{ id: genId(), content: block?.textRaw ?? " " }],
      });
    } else if (block.dataType === "table") {
      items.push({
        id: block._id || genId(),
        type: "table",
        tableTitle: block.tableTitle || "Untitled Table",
        tableRow: Array.isArray(block.tableRow) ? block.tableRow.map((r) => ({ ...r })) : [],
        tableColumn: Array.isArray(block.tableColumn) ? block.tableColumn.map((c) => ({ ...c })) : [],
        tableType: block?.tableType ?? "table",
        table: block?.table ?? null,
        hasCellData: block?.tableType === "content",
      });
    }
  });
  return items;
};

const cleanTableData = (di, section) => {
  const hideKeys = ["issubrow", "isnewrow", "isnew", "name", "formula"];
  const filterObj = (obj) => {
    if (!obj) return obj;
    const newObj = { ...obj };
    Object.keys(newObj).forEach(key => {
      if (hideKeys.includes(key.toLowerCase())) {
        delete newObj[key];
      }
    });
    return newObj;
  };
  return {
    tableTitle: di.tableTitle,
    tableType: di.tableType,
    hasCellData: di.hasCellData,
    tableColumn: (di.tableColumn || []).filter(c => !hideKeys.includes((c.name || "").toLowerCase())),
    tableRow: (di.tableRow || []).map(filterObj),
    table: Array.isArray(di.table) ? di.table.map(filterObj) : di.table,
  };
};

const StandaloneBin = ({
  binSections,
  activeBinSection,
  setActiveBinSection,
  activeBinTab,
  setActiveBinTab,
  deletedItems,
  setDeletedItems,
  selectedBinItems,
  setSelectedBinItems,
  renderSubGroups,
  project_id,
  onRestoreSuccess,
  updateTrigger
}) => {
  const [apiDeletedItems, setApiDeletedItems] = useState([]);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const refreshDeletedItems = () => {
    if (!project_id) return Promise.resolve();

    return getPopulateFSDeletedItemsApiHandler({ projectID: project_id })
      .then((res) => {
        if (res?.data?.success || res?.data?.data) {
          const fetchedData = res.data.data || res.data.deletedItems || [];
          
          const flattenedData = [];

          const processItem = (item, parentDeletedId, parentCategory, accumulatedParents = []) => {
             const category = parentCategory || item.category || item.section || item.statementType || item.fsType;
             
             // Base parents: if it's the root API item, it might have parentNames
             const baseParents = parentDeletedId ? accumulatedParents : (item.parentNames || []);
             
             flattenedData.push({
               ...item,
               // Do not overwrite _id so that children can be selected individually
               category: category,
               isChild: !!parentDeletedId,
               hierarchyParents: baseParents,
               parentDeletedId: parentDeletedId
             });

             const currentName = item.name || item.title || "";
             const nextParents = [...baseParents, currentName];
             
             // The deleted ID to pass down to children
             const nextParentId = parentDeletedId || item._id;

             if (item.data && item.data.node && Array.isArray(item.data.node.subgroup)) {
                 item.data.node.subgroup.forEach(child => processItem(child, nextParentId, category, nextParents));
             } else if (Array.isArray(item.subgroup)) {
                 item.subgroup.forEach(child => processItem(child, nextParentId, category, nextParents));
             }
          };

          fetchedData.forEach(item => processItem(item, null, null, []));

          let mapped = flattenedData.map((item, idx) => ({
            id: item._id, // use the deleted record ID so restore works
            uniqueKey: `${item._id}_${idx}`, // unique key for rendering lists
            section: getSectionFromDeletedItem(item),
            tab: getTabFromDeletedItem(item),
            title: getTitleFromDeletedItem(item),
            levelLabel: getTabFromDeletedItem(item),
            hierarchyParents: item.hierarchyParents || [],
            isChild: !!item.isChild,
            noteData: item,
            isApiItem: true,
            rawItem: item,
          }));

          mapped = mapped.map(item => {
            if (item.isChild) return item;

            const safeCompare = (str1, str2) => {
              if (!str1 || !str2) return false;
              return str1.toString().trim().toLowerCase() === str2.toString().trim().toLowerCase();
            };

            let hasIndependentParent = false;
            if (item.tab === "Level 2") {
              hasIndependentParent = mapped.some(parent => 
                parent.tab === "Level 1" && 
                ((item.rawItem?.fsGroupID && parent.id === item.rawItem?.fsGroupID) || 
                 safeCompare(parent.title, item.rawItem?.fsGroupName) || safeCompare(parent.title, item.rawItem?.fs_grp) || safeCompare(parent.title, item.hierarchyParents?.[0]))
              );
            } else if (item.tab === "Level 3") {
              hasIndependentParent = mapped.some(parent => 
                (parent.tab === "Level 2" && 
                  ((item.rawItem?.noteGroupID && parent.id === item.rawItem?.noteGroupID) || 
                   safeCompare(parent.title, item.rawItem?.noteGroupName) || safeCompare(parent.title, item.rawItem?.note_grp) || safeCompare(parent.title, item.hierarchyParents?.[1] || item.hierarchyParents?.[item.hierarchyParents?.length - 1]))
                ) || 
                (parent.tab === "Level 1" && 
                  ((item.rawItem?.fsGroupID && parent.id === item.rawItem?.fsGroupID) || 
                   safeCompare(parent.title, item.rawItem?.fsGroupName) || safeCompare(parent.title, item.rawItem?.fs_grp) || safeCompare(parent.title, item.hierarchyParents?.[0]))
                )
              );
            }

            if (hasIndependentParent) {
              return { ...item, isChild: true };
            }
            return item;
          });

          setApiDeletedItems(mapped);

          let currentCatTabs = mapped
            .filter((di) => di.section === currentBinCat)
            .map((di) => di.tab);
            
          if (currentBinCat === "BS notes" || currentBinCat === "PL notes") {
            const targetCat = currentBinCat === "BS notes" ? "BS" : "PL";
            currentCatTabs = mapped
              .filter((di) => di.section === targetCat && di.tab === 'Level 2' && !di.isChild)
              .map(di => di.title);
          }

          const hasFixed = ['BS', 'PL', 'Cashflow'].includes(currentBinCat);
          const allowedTabs = hasFixed 
            ? ['Level 1', 'Level 2', 'Level 3']
            : Array.from(new Set(currentCatTabs));

          if (allowedTabs.length > 0 && !allowedTabs.includes(activeBinTab)) {
            setActiveBinTab(allowedTabs[0]);
          } else if (allowedTabs.length === 0 && !hasFixed) {
            setActiveBinTab('');
          }
        }
      })
      .catch((err) => console.error("Error fetching deleted items", err));
  };

  const normalizeText = (value) => String(value || "").trim().toLowerCase();

  const getSectionFromDeletedItem = (item) => {
    const categoryCandidates = [
      item?.category,
      item?.section,
      item?.statementType,
      item?.fsType,
      item?.type,
      item?.itemType,
    ]
      .filter(Boolean)
      .map(normalizeText);

    if (categoryCandidates.some((value) => value === "bs" || value === "balance sheet" || value === "balancesheet")) {
      return "BS";
    }
    if (categoryCandidates.some((value) => value === "pl" || value === "p&l statement" || value === "pnl statement" || value === "profit and loss")) {
      return "PL";
    }
    if (categoryCandidates.some((value) => value === "general notes" || value === "general note" || value === "generalnote")) {
      return "general notes";
    }
    if (categoryCandidates.some((value) => value === "disclosure" || value === "disclosures")) {
      return "disclosure";
    }
    if (categoryCandidates.some((value) => value === "bs notes" || value === "balance sheet notes")) {
      return "BS notes";
    }
    if (categoryCandidates.some((value) => value === "pl notes" || value === "p&l statement notes" || value === "pnl statement notes")) {
      return "PL notes";
    }
    return "";
  };

  const getTabFromDeletedItem = (item) => {
    if ((item?.itemType === "generalNoteSubgroup" || item?.itemType === "disclosureSubgroup") && item?.parentNames && item.parentNames.length > 0) {
      return item.parentNames[0];
    }

    const explicitLevel = Number(item?.level ?? item?.deletedLevel ?? item?.hierarchyLevel);
    if ([1, 2, 3].includes(explicitLevel)) {
      return `Level ${explicitLevel}`;
    }

    if (item?.levelThreeID || item?.levelThreeName || item?.subGroupID || item?.subGroupName) {
      return "Level 3";
    }
    if (item?.levelTwoID || item?.levelTwoName || item?.noteGroupID || item?.noteGroupName) {
      return "Level 2";
    }
    if (item?.levelOneID || item?.levelOneName || item?.fsGroupID || item?.fsGroupName) {
      return "Level 1";
    }

    return item?.name || item?.title || "Untitled";
  };

  const getTitleFromDeletedItem = (item) => {
    const levelTab = getTabFromDeletedItem(item);

    if (levelTab === "Level 3") {
      return (
        item?.name ||
        item?.title ||
        item?.levelThreeName ||
        item?.subGroupName ||
        item?.sub_grp ||
        "Untitled"
      );
    }

    if (levelTab === "Level 2") {
      return (
        item?.name ||
        item?.title ||
        item?.levelTwoName ||
        item?.noteGroupName ||
        item?.note_grp ||
        "Untitled"
      );
    }

    if (levelTab === "Level 1") {
      return (
        item?.name ||
        item?.title ||
        item?.levelOneName ||
        item?.fsGroupName ||
        item?.fs_grp ||
        "Untitled"
      );
    }

    return item?.name || item?.title || "Untitled";
  };

  const getCatFromBinSection = (section) => {
    switch(section) {
      case "Balance Sheet": return "BS";
      case "P&L Statement": return "PL";
      case "General Notes": return "general notes";
      case "Disclosures": return "disclosure";
      case "Balance Sheet Notes": return "BS notes";
      case "P&L Statement Notes": return "PL notes";
      default: return section;
    }
  };

  const currentBinCat = getCatFromBinSection(activeBinSection);
  const deletedItemsForSection = deletedItems.filter(di => di.section === currentBinCat);

  let binTabs = [];
  if (activeBinSection === 'Balance Sheet' || activeBinSection === 'P&L Statement' || activeBinSection === 'Cashflow') {
    binTabs = ['Level 1', 'Level 2', 'Level 3'];



  } else {
    binTabs = Array.from(new Set(deletedItemsForSection.map(di => di.tab)));
  }

  const itemsToRenderInBin = deletedItemsForSection.filter(di => di.tab === activeBinTab);

  let serverItemsForSection = apiDeletedItems.filter((di) => di.section === currentBinCat);
  const isNotesSection = activeBinSection === "Balance Sheet Notes" || activeBinSection === "P&L Statement Notes";
  
  if (isNotesSection) {
    const targetCat = activeBinSection === "Balance Sheet Notes" ? "BS" : "PL";
    // For notes, we display Level 2 items from BS/PL as tabs
    serverItemsForSection = apiDeletedItems.filter((di) => di.section === targetCat && di.tab === 'Level 2' && !di.isChild);
  }

  const usesServerBin = serverItemsForSection.length > 0 || ["General Notes", "Disclosures", "Balance Sheet", "P&L Statement"].includes(activeBinSection) || isNotesSection;

  useEffect(() => {
    refreshDeletedItems();
  }, [activeBinSection, project_id, activeBinTab, currentBinCat, setActiveBinTab, updateTrigger]);

  let serverTabs = Array.from(new Set(serverItemsForSection.map(di => di.tab)));
  if (isNotesSection) {
    serverTabs = Array.from(new Set(serverItemsForSection.map(di => di.title)));
  }

  const hasFixedTabs = ['Balance Sheet', 'P&L Statement', 'Cashflow'].includes(activeBinSection);
  let derivedTabs = hasFixedTabs
    ? binTabs
    : (usesServerBin ? (serverTabs.length > 0 ? serverTabs : binTabs) : binTabs);
    

  let displayedItems = usesServerBin
    ? serverItemsForSection.filter(di => di.tab === activeBinTab)
    : itemsToRenderInBin;

  if (isNotesSection) {
    // displayedItems is exactly the L2 item whose title matches activeBinTab
    displayedItems = serverItemsForSection.filter(di => di.title === activeBinTab);
  }

  const handleRestoreBinItem = async (id) => {
    setRestoreConfirm({
      title: "Restore Item",
      message: "Are you sure you want to restore this item?",
      onConfirm: async () => {
        setRestoreConfirm(null);
        if (usesServerBin) {
          try {
            await restorePopulateFSDeletedItemsApiHandler({ projectID: project_id }, { deletedItemID: [id] });
            if (onRestoreSuccess) onRestoreSuccess();
            await refreshDeletedItems();
            setSuccessMessage("Item successfully restored!");
            setShowSuccessPopup(true);
          } catch (err) {
            console.error("Error restoring item", err);
          }
        } else {
          setDeletedItems(prev => prev.filter(di => di.id !== id));
          setSuccessMessage("Item successfully restored!");
          setShowSuccessPopup(true);
        }
      }
    });
  };
  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: '#fff', margin: '10px 0 20px 0', height: 'calc(100vh - 100px)', display: 'flex', borderRadius: '14px', border: '1px solid #eaeeed', overflow: 'hidden', }}>
      <style>
        {`
          .bin-child-tooltip-container {
            position: relative;
            display: inline-flex;
            align-items: center;
          }
          .bin-child-tooltip {
            visibility: hidden;
            background-color: #111111;
            color: #fff;
            text-align: center;
            border-radius: 4px;
            padding: 8px 12px;
            position: absolute;
            z-index: 10;
            top: 50%;
            left: 100%;
            transform: translateY(-50%);
            margin-left: 10px;
            opacity: 0;
            transition: opacity 0.3s;
            white-space: nowrap;
            font-size: 13px;
            box-shadow: 0px 4px 10px rgba(0,0,0,0.25);
          }
          .bin-child-tooltip::after {
            content: "";
            position: absolute;
            top: 50%;
            right: 100%;
            margin-top: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: transparent #111111 transparent transparent;
          }
          .bin-child-tooltip-container:hover .bin-child-tooltip {
            visibility: visible;
            opacity: 1;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      {/* Left Menu Container */}
      <div style={{ width: '250px', display: 'flex', flexDirection: 'column', borderRight: '1px solid #eaeeed', overflowY: 'auto' }} className="hide-scrollbar">
        {binSections.map((section, idx) => (
          <div 
            key={section}
            onClick={() => {
              setActiveBinSection(section);
              const newCat = getCatFromBinSection(section);
              const sectionItems = deletedItems.filter(di => di.section === newCat);
              if (section === 'Balance Sheet' || section === 'P&L Statement' || section === 'Cashflow') {
                setActiveBinTab('Level 1');
              } else if (section === 'Balance Sheet Notes' || section === 'P&L Statement Notes') {
                const targetCat = section === 'Balance Sheet Notes' ? 'BS' : 'PL';
                const noteTabs = Array.from(new Set(apiDeletedItems.filter(di => di.section === targetCat && di.tab === 'Level 2' && !di.isChild).map(di => di.title)));
                setActiveBinTab(noteTabs.length > 0 ? noteTabs[0] : '');
              } else {
                const serverTabsForCat = Array.from(new Set(apiDeletedItems.filter(di => di.section === newCat).map(di => di.tab)));
                const uniqueTabs = serverTabsForCat.length > 0 ? serverTabsForCat : Array.from(new Set(sectionItems.map(di => di.tab)));
                setActiveBinTab(uniqueTabs.length > 0 ? uniqueTabs[0] : '');
              }
            }}
            style={{
              padding: '15px 20px',
              cursor: 'pointer',
              background: activeBinSection === section ? '#E4F2E6' : 'transparent',
              borderRight: activeBinSection === section ? '1px solid #205a46' : 'transparent',
              color: activeBinSection === section ? '#205a46' : '#333',
              fontSize: '14px',
              fontWeight: activeBinSection === section ? 600 : 400
            }}
          >
            {section}
          </div>
        ))}
      </div>

      {/* Right Content Container */}
      <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #eaeeed', borderRadius: '8px', background: '#fff', overflow: 'hidden' }}>
        {derivedTabs.length > 0 && (
        <div style={{ display: 'flex', borderBottom: '1px solid #eaeeed', flexWrap: 'wrap' }}>
          {derivedTabs.map((tab) => (
            <div 
              key={tab}
              onClick={() => setActiveBinTab(tab)}
              style={{ 
                padding: '15px 30px', 
                background: activeBinTab === tab ? '#E4F2E6' : 'transparent',
                borderBottom: activeBinTab === tab ? '1px solid #205a46' : 'transparent', 
                color: activeBinTab === tab ? '#205a46' : '#666', 
                fontWeight: activeBinTab === tab ? 600 : 500, 
                fontSize: '14px', 
                cursor: 'pointer'
              }}
            >
              {tab}
            </div>
          ))}
        </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', padding: '20px', flex: 1, overflow: 'hidden' }}>
          {!isNotesSection && usesServerBin && displayedItems.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '15px', borderBottom: '1px solid #f0f0f0' }}>
              <input 
                type="checkbox" 
                checked={displayedItems.length > 0 && selectedBinItems.length === displayedItems.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedBinItems(displayedItems.map(di => di.id));
                  } else {
                    setSelectedBinItems([]);
                  }
                }}
                style={{ accentColor: '#205a46', width: '16px', height: '16px' }} 
              />
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                Select All
              </span>
            </div>
          )}
          {!isNotesSection && !usesServerBin && displayedItems.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '15px', borderBottom: '1px solid #f0f0f0' }}>
              <input 
                type="checkbox" 
                checked={selectedBinItems.length === displayedItems.length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedBinItems(displayedItems.map(di => di.id));
                  } else {
                    setSelectedBinItems([]);
                  }
                }}
                style={{ accentColor: '#205a46', width: '16px', height: '16px' }} 
              />
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#333' }}>
                Select All
              </span>
            </div>
          )}
          {isNotesSection && displayedItems.length > 0 && (
            <div style={{ paddingBottom: '15px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <InfoPrimaryIcon style={{ width: '16px', height: '16px' }} />
                  <span style={{ fontSize: '14px', color: '#111111', fontWeight: 400 }}>
                    The below tables can only be restored if the associated Level 2 is restored from the {activeBinSection === "Balance Sheet Notes" ? "Balance Sheet" : "P&L Statement"} Tab
                  </span>
                </div>
                <div 
                  style={{ color: '#205a46', fontSize: '14px', fontWeight: 600, cursor: 'pointer', textDecoration: 'none' }}
                  onClick={() => {
                    setActiveBinSection(activeBinSection === "Balance Sheet Notes" ? "Balance Sheet" : "P&L Statement");
                    setActiveBinTab("Level 2");
                  }}
                >
                  Restore from Level 2
                </div>
              </div>
            </div>
          )}
          <div className="hide-scrollbar" style={{ flex: 1, overflowY: 'auto', paddingRight: '5px' }}>
          {displayedItems.map((di, idx) => (
            <div key={di.uniqueKey || di.id || idx} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '15px 0', borderBottom: !usesServerBin ? '1px solid #f0f0f0' : '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                {!isNotesSection && (
                  <div style={{ marginTop: '10px', opacity: di.isChild ? 0.6 : 1 }}>
                    <input 
                      type="checkbox" 
                      checked={selectedBinItems.includes(di.id)}
                      onChange={(e) => {
                        if (di.isChild) {
                          const parentLevel = di.tab === 'Level 3' ? 'level 2' : 'level 1';
                          setErrorMessage(`No ${parentLevel} to restore to. ${parentLevel.charAt(0).toUpperCase() + parentLevel.slice(1)} has been deleted`);
                          setShowErrorPopup(true);
                          return;
                        }
                        if (e.target.checked) {
                          setSelectedBinItems(prev => [...new Set([...prev, di.id])]);
                        } else {
                          setSelectedBinItems(prev => prev.filter(item => item !== di.id));
                        }
                      }}
                      style={{ accentColor: '#205a46', width: '16px', height: '16px', cursor: di.isChild ? 'not-allowed' : 'pointer' }} 
                    />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  {di.isApiItem && ["BS", "PL"].includes(di.section) && !isNotesSection ? (
                    <div style={{ paddingTop: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '15px', fontWeight: 400, color: '#111111', opacity: di.isChild ? 0.6 : 1 }}>
                          {di.title}
                        </div>
                        {di.isChild && (
                          <div className="bin-child-tooltip-container">
                            <InfoGreyIcon style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                            <span className="bin-child-tooltip">
                              No {di.tab === 'Level 3' ? 'level 2' : 'level 1'} to restore to.<br />
                              {di.tab === 'Level 3' ? 'Level 2' : 'Level 1'} has been deleted
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {di.hierarchyParents && di.hierarchyParents.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', flexWrap: 'wrap', opacity: di.isChild ? 0.6 : 1 }}>
                          {di.hierarchyParents.map((parentName, parentIdx) => (
                            <React.Fragment key={parentIdx}>
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                padding: '2px 8px', borderRadius: '4px',
                                background: '#f4faf5', border: '1px solid #c2e6d1'
                              }}>
                                <span style={{ fontSize: '12px', fontWeight: 600, color: '#809790' }}>
                                  LV{parentIdx + 1}
                               </span>
                               <span style={{ fontSize: '13px', fontWeight: 500, color: '#152521' }}>
                                 {parentName}
                               </span>
                              </div>
                              {parentIdx < di.hierarchyParents.length - 1 && (
                                <img src={arrowRightIcon} alt="arrow" style={{ width: '12px', height: '12px', objectFit: 'contain' }} />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '13px', color: '#4D6D64', marginTop: '4px' }}>
                          {di.levelLabel}
                        </div>
                      )}
                    </div>
                  ) : di.isApiItem ? (
                    <div style={{ flex: 1, pointerEvents: 'none', opacity: 0.8, overflow: 'hidden' }}>
                      {(() => {
                        const doc = di.noteData;
                        let items = [];
                        if (doc.data?.notes && Array.isArray(doc.data.notes)) {
                          const allNoteBlocks = doc.data.notes.flatMap(n => n.subgroup || []);
                          items = normalizeFromInitial({ data: allNoteBlocks });
                          
                          // Inject noteTableRows into the tables
                          if (doc.data.noteTableRows && Array.isArray(doc.data.noteTableRows)) {
                            items.forEach(item => {
                              if (item.type === 'table' && Array.isArray(item.table)) {
                                const rowsToInject = doc.data.noteTableRows
                                  .filter(r => r.tableTitle === item.tableTitle)
                                  .map(r => r.row);
                                if (rowsToInject.length > 0) {
                                  // Prepend injected rows to the existing table rows (which are usually Totals)
                                  item.table = [...rowsToInject, ...item.table];
                                }
                              }
                            });
                          }
                        } else if ((doc.itemType === "generalNoteSubgroup" || doc.itemType === "disclosureSubgroup") && doc.data?.block) {
                          items = normalizeFromInitial({ data: [doc.data.block] });
                        } else if (doc.data?.section?.subgroup) {
                          items = normalizeFromInitial({ data: doc.data.section.subgroup });
                        } else if (Array.isArray(doc.data)) {
                          items = normalizeFromInitial({ data: doc.data });
                        } else if (doc.data && Array.isArray(doc.data.data)) {
                          items = normalizeFromInitial(doc.data);
                        } else if (doc.data) {
                          items = normalizeFromInitial({ data: [doc.data] });
                        } else if (Array.isArray(doc.subgroup) || Array.isArray(doc.components)) {
                          items = normalizeFromInitial({ data: doc.subgroup || doc.components });
                        }

                        return items.map((blockItem) => (
                          <div key={blockItem.id || blockItem._id} style={{ display: "flex", alignItems: "flex-start", gap: "15px", padding: "15px 0", width: "100%" }}>
                            <div style={{ flex: 1, overflow: "hidden" }}>
                              {blockItem.type === "table" && (
                                <div style={{ background: "#f8f9f9", display: "flex", alignItems: "center", padding: "8px 15px", borderRadius: "10px 10px 0 0", border: "1px solid #ccd5d3", borderBottom: "none", justifyContent: "space-between" }}>
                                  <div style={{ fontWeight: 600, color: "#333", fontSize: "14px" }}>
                                    {blockItem.tableTitle || "Table"}
                                  </div>
                                </div>
                              )}
                              {blockItem.type === "text" ? (
                                <QuillReactEditor
                                  value={blockItem.text?.[0]?.content ?? blockItem.textRaw ?? ""}
                                  onChange={() => {}}
                                  disabled={true}
                                  rightControl={null}
                                />
                              ) : blockItem.type === "table" ? (
                                <div style={{ border: "none", borderRadius: "0 0 10px 10px", background: "#fff", padding: 0, overflow: "hidden" }}>
                                  <TemplateTableGenerator
                                    forceOpen={false}
                                    hideRowBoldIcon={true}
                                    value={cleanTableData(blockItem, activeBinSection)}
                                    onChange={() => {}}
                                    onAfterOpen={() => {}}
                                    disabled={true}
                                    hideHeader={true}
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    renderSubGroups({ ...di.noteData, subgroup: [di.data] }, currentBinCat)
                  )}
                </div>
              </div>
              {!isNotesSection && !usesServerBin && (
                <RestoreIcon 
                  onClick={() => handleRestoreBinItem(di.id)}
                  style={{ cursor: 'pointer', color: '#205a46', marginTop: '10px' }} 
                  title="Restore"
                />
              )}
            </div>
          ))}
          {displayedItems.length === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                minHeight: "300px",
              }}
            >
              <EmptyIcon />
              <div style={{ color: "#183930", fontWeight: 600, fontSize: "20px", marginTop: "15px" }}>
                Nothing here yet
              </div>
              <div style={{ color: "#4D6D64", fontWeight: 400, fontSize: "16px", marginTop: "5px" }}>
                There are no deleted items
              </div>
            </div>
          )}
          </div>
          </div>
        </div>
      </div>
      <ErrorPopup
        isOpen={showErrorPopup}
        message={errorMessage}
        onClose={() => setShowErrorPopup(false)}
      />
      <RestorePopup
        isOpen={restoreConfirm !== null}
        title={restoreConfirm?.title}
        message={restoreConfirm?.message}
        onConfirm={restoreConfirm?.onConfirm}
        onCancel={() => setRestoreConfirm(null)}
      />
      <SuccessPopup
        isOpen={showSuccessPopup}
        message={successMessage}
        onClose={() => setShowSuccessPopup(false)}
      />
    </div>
  );
};

export default StandaloneBin;
